import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface AdminSession {
  isLoggedIn: boolean;
  userId: string | null;
  userName: string | null;
}

interface CustomerSession {
  isLoggedIn: boolean;
  customerType: "registered" | "guest" | null;
  userId: string | null;
  userName: string | null;
}

interface AuthState {
  admin: AdminSession;
  customer: CustomerSession;
}

const initialState: AuthState = {
  admin: {
    isLoggedIn: false,
    userId: null,
    userName: null,
  },

  customer: {
    isLoggedIn: false,
    customerType: null,
    userId: null,
    userName: null,
  },
};

const authSlice = createSlice({
  name: "auth",

  initialState,

  reducers: {
    // --------------------------------------------
    // Backward-compatible login
    // --------------------------------------------

    setLogin: (
      state,
      action: PayloadAction<{
        token?: string;
        role: "admin" | "customer";
        customerType?: "registered" | "guest";
        userId: string;
        userName?: string;
      }>,
    ) => {
      const { role, customerType, userId, userName } = action.payload;

      if (role === "admin") {
        state.admin.isLoggedIn = true;
        state.admin.userId = userId;
        state.admin.userName = userName || "Admin";
      }

      if (role === "customer") {
        state.customer.isLoggedIn = true;
        state.customer.userId = userId;
        state.customer.userName = userName || "User";
        state.customer.customerType = customerType || "registered";
      }
    },

    // --------------------------------------------
    // Admin Login
    // --------------------------------------------

    setAdminLoggedIn: (
      state,
      action: PayloadAction<{
        userId: string;
        userName?: string;
      }>,
    ) => {
      state.admin.isLoggedIn = true;
      state.admin.userId = action.payload.userId;
      state.admin.userName = action.payload.userName || "Admin";
    },

    // --------------------------------------------
    // Customer Login
    // --------------------------------------------

    setCustomerLoggedIn: (
      state,
      action: PayloadAction<{
        userId: string;
        userName?: string;
        customerType: "registered" | "guest";
      }>,
    ) => {
      state.customer.isLoggedIn = true;
      state.customer.userId = action.payload.userId;
      state.customer.userName = action.payload.userName || "User";
      state.customer.customerType = action.payload.customerType;
    },

    // --------------------------------------------
    // Admin Logout
    // --------------------------------------------

    logoutAdmin: (state) => {
      state.admin.isLoggedIn = false;
      state.admin.userId = null;
      state.admin.userName = null;
    },

    // --------------------------------------------
    // Customer Logout
    // --------------------------------------------

    logoutCustomer: (state) => {
      state.customer.isLoggedIn = false;
      state.customer.customerType = null;
      state.customer.userId = null;
      state.customer.userName = null;
    },

    // --------------------------------------------
    // Logout Everything
    // --------------------------------------------

    logoutAll: (state) => {
      state.admin.isLoggedIn = false;
      state.admin.userId = null;
      state.admin.userName = null;

      state.customer.isLoggedIn = false;
      state.customer.customerType = null;
      state.customer.userId = null;
      state.customer.userName = null;
    },
  },
});

export const {
  setLogin,
  setAdminLoggedIn,
  setCustomerLoggedIn,
  logoutAdmin,
  logoutCustomer,
  logoutAll,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
